import axios from "axios";

/**
 * BlendedNominationService class to handle SME nominations for blended courses.
 */
class BlendedNominationService {
  static BASE_URL = process.env.REACT_APP_BASE_URL1;


  /**
   * Gets programs that are open for nomination and belong to the user's track.
   * @param {string} month - Month in YYYY-MM format
   * @param {string} track - User's track
   * @returns {Promise<Array>} The list of programs open for nomination for the user's track
   * @throws Will throw an error if the request fails.
   */
  static async getBlendedProgramsByMonthAndTrack(month, track) {
    try {
      const response = await axios.get(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/programs/${month}/${track}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Gets programs that are open for nomination and belong to the user's track.
   * @param {string} month - Month in YYYY-MM format
   * @param {string} track - User's track
   * @returns {Promise<Array>} The list of programs open for nomination for the user's track
   * @throws Will throw an error if the request fails.
   */
  static async getBlendedProgramsOpenForNominationByMonthAndTrack(month, track) {
    try {
      const response = await axios.get(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/programs/open-nominations/${month}/${track}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Submits SME nomination for blended learning programs.
   * @param {Object} payload - The nomination payload containing program and SME details
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async submitSMENomination(payload) {
    try {
      const response = await axios.put(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/sme-nomination`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Finalizes blended nominations for a specific month.
   * @param {string} month - Month in YYYY-MM format
   * @param {Array} finalizationData - Array of finalization data objects
   * @returns {Promise<string>} Success message from the server
   * @throws Will throw an error if the request fails.
   */
  static async finalizeBlendedNomination(month, finalizationData) {
    const [year, monthNum] = month.split("-");
    try {
      const response = await axios.put(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/finalize-nominations/${year}/${monthNum}`,
        finalizationData,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Drops SME nomination for an entire program.
   * @param {Object} payload - The drop payload containing program and SME details
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async dropSMENomination(payload) {
    try {
      const response = await axios.delete(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/sme-nomination`,
        { 
          data: payload,
          headers: { "Content-Type": "application/json" } 
        }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

    /**
   * Drops SME nomination for a specific connect.
   * @param {Object} payload - The drop payload containing program, module, and connect details
   * @returns {Promise<Object>} The response data from the server
   * @throws Will throw an error if the request fails.
   */
  static async dropSingleSMEConnect(payload) {
    try {
      const response = await axios.delete(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/sme-connect-nomination`,
        { 
          data: payload,
          headers: { "Content-Type": "application/json" } 
        }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Gets blended SME nominations by user and month for status checking.
   * @param {Object} user - User object containing track and email
   * @param {string} month - Month in YYYY-MM format
   * @returns {Promise<Array>} The list of blended nominations for the user in the specified month
   * @throws Will throw an error if the request fails.
   */
  static async getBlendedSMENominationsByUserAndMonth(user, month) {
    try {
      const response = await axios.get(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/nominations/${user.track}/${user.email}/${month}`
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }

  /**
   * Cancels a blended SME nomination.
   * @param {Object} payload - The cancellation payload containing nomination details
   * @returns {Promise<boolean>} True if cancellation was successful
   * @throws Will throw an error if the request fails.
   */
  static async cancelBlendedNomination(payload) {
    try {
      const response = await axios.put(
        `${BlendedNominationService.BASE_URL}/sme/api/blended/cancel-nomination`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      return response.data;
    } catch (error) {
      console.error("API Error:", error.response || error.message);
      throw new Error(
        error.response?.data?.message || "Failed to communicate with the server."
      );
    }
  }


}

export default BlendedNominationService;